import React, { useState, useRef, ChangeEvent, KeyboardEvent, ClipboardEvent } from 'react';

interface VerificationInputProps {
  length: number;
  loading: boolean;
  onComplete: (code: string) => void;
}

const VerificationInput: React.FC<VerificationInputProps> = ({ length, loading, onComplete }) => {
  const [code, setCode] = useState<string[]>(new Array(length).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>(new Array(length).fill(null)); 

  const processInput = (value: string, index: number) => {
    const newCode = [...code];
    newCode[index] = value; 
    setCode(newCode);

    if (value && index < length - 1) { 
      inputsRef.current[index + 1]?.focus();
    }

    const fullCode = newCode.join('');
    if (fullCode.length === length && newCode.every(char => char !== '')) { 
      onComplete(fullCode);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const inputValue = e.target.value;
    const processedDigit = (inputValue.match(/[0-9]/) || [''])[0];

    if (code[index] !== processedDigit) {
      processInput(processedDigit, index);
    } else if (processedDigit && index < length - 1) {
   
      processInput(processedDigit, index);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) { 
        inputsRef.current[index - 1]?.focus();
      } else if (code[index]) {

      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedString = e.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '') 
      .slice(0, length);      

    if (pastedString) {
      const newCodeState = [...code]; 
      const charsToPaste = pastedString.split('');

      let lastFilledIndex = -1;
      const focusedInput = inputsRef.current.findIndex(input => input === document.activeElement);
      let currentPastingIndex = (focusedInput !== -1) ? focusedInput : 0;


      for (let i = 0; i < charsToPaste.length && currentPastingIndex < length; i++) {
        newCodeState[currentPastingIndex] = charsToPaste[i];
        lastFilledIndex = currentPastingIndex;
        currentPastingIndex++;
      }

      setCode(newCodeState);

      if (lastFilledIndex < length - 1 && lastFilledIndex > -1) {
        inputsRef.current[lastFilledIndex + 1]?.focus();
      } else if (lastFilledIndex === length - 1 || charsToPaste.length === length) {
        inputsRef.current[Math.min(lastFilledIndex, length - 1)]?.focus();
      }
      const isComplete = newCodeState.every(char => char !== '');
      if (isComplete) {
        onComplete(newCodeState.join(''));
      }
    }
  };

  return (
    <div className="verification-input-boxes">
      {Array.from({ length }).map((_, index) => ( 
        <input
          key={index}
          ref={el => {
            if (el) inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={code[index]} 
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          disabled={loading}
          autoFocus={index === 0}
          className="verification-box"
        />
      ))}
    </div>
  );
};

export default VerificationInput;